import React, { useState, useRef } from 'react';
import { userAPI, adminAPI } from '../services/api';
import Icon from './Icon';

/**
 * BikeImageGallery - Manages bike images with upload, delete, set profile features
 * @param {Object} props
 * @param {number} props.bikeId - The bike ID
 * @param {Array} props.images - Array of image objects
 * @param {string} props.profileImage - Current profile image filename
 * @param {boolean} props.isAdmin - Whether user is admin
 * @param {boolean} props.canEdit - Whether user can edit (owner or admin)
 * @param {Function} props.onRefresh - Callback to refresh bike data
 */
function BikeImageGallery({ bikeId, images = [], profileImage, isAdmin = false, canEdit = true, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [editingCaption, setEditingCaption] = useState(null);
  const [captionText, setCaptionText] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name || '';
    const extension = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
    const allowedExtensions = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif']);
    const isImageMime = Boolean(file.type && file.type.startsWith('image/'));
    const isAllowedExtension = allowedExtensions.has(extension);

    // Validate file type (supports iOS camera files where mime can be missing)
    if (!isImageMime && !isAllowedExtension) {
      setError('Only image files are allowed (JPG, PNG, GIF, WEBP, HEIC, HEIF)');
      return;
    }

    // Validate file size (20MB cap)
    if (file.size > 20 * 1024 * 1024) {
      setError('File size cannot exceed 20MB');
      return;
    }

    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('image', file);

    try {
      if (isAdmin) {
        await adminAPI.uploadBikeImage(bikeId, formData);
      } else {
        await userAPI.uploadBikeImage(bikeId, formData);
      }
      setSuccess('Image uploaded successfully!');
      if (onRefresh) onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      if (isAdmin) {
        await adminAPI.deleteBikeImage(bikeId, imageId);
      } else {
        await userAPI.deleteBikeImage(bikeId, imageId);
      }
      setSuccess('Image deleted!');
      if (onRefresh) onRefresh();
      setSelectedImage(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete image');
    }
  };

  const handleSetAsProfile = async (imageId) => {
    try {
      if (isAdmin) {
        await adminAPI.setBikeProfileImage(bikeId, imageId);
      } else {
        await userAPI.setBikeProfileImage(bikeId, imageId);
      }
      setSuccess('Profile image set!');
      if (onRefresh) onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set profile image');
    }
  };

  const handleUpdateCaption = async (imageId) => {
    try {
      await userAPI.updateBikeImageCaption(bikeId, imageId, captionText);
      setSuccess('Caption saved!');
      setEditingCaption(null);
      setCaptionText('');
      if (onRefresh) onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save caption');
    }
  };

  const getImageUrl = (image) => {
    if (!image) return '';
    // Prefer backend-provided absolute URL (S3/Bucketeer), fallback to legacy API path
    return image.file_url || image.file_path || (image.filename ? `/api/user/bikes/${bikeId}/images/${image.filename}` : '');
  };

  return (
    <div style={{
      background: 'var(--bg-overlay-light)',
      border: '1px solid var(--border)',
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon name="photos" color="primary" darkMode={true} size={18} />
          Images
        </h2>
        {canEdit && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn btn-sm btn-primary"
              style={{ padding: '8px 16px', fontSize: '13px' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {uploading ? (
                  <>
                    <Icon name="loading" color="primary" darkMode={true} size={14} />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Icon name="add_circle" color="primary" darkMode={true} size={14} />
                    Add image
                  </>
                )}
              </span>
            </button>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          padding: '12px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#f87171',
          fontSize: '13px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px',
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#4A8C18',
          fontSize: '13px'
        }}>
          {success}
        </div>
      )}

      {images.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--text-muted)'
        }}>
          <Icon name="photos" color="muted" darkMode={true} size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
          <p style={{ margin: 0, fontSize: '14px' }}>
            {canEdit ? 'No images yet. Click "Add Image" to upload.' : 'No images of this bike.'}
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: '12px'
        }}>
          {images.map((image) => (
            <div
              key={image.id}
              style={{
                position: 'relative',
                aspectRatio: '1',
                borderRadius: '12px',
                overflow: 'hidden',
                border: image.is_profile || image.filename === profileImage
                  ? '3px solid var(--primary)'
                  : '1px solid var(--border)',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
              }}
              onClick={() => setSelectedImage(image)}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <img
                src={getImageUrl(image)}
                alt={image.caption || 'Bike image'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const errorDiv = document.createElement('div');
                  errorDiv.style.cssText = 'display:flex;align-items:center;justify-content:center;height:100%;background:var(--bg-overlay-light);color:var(--text-muted);';
                  errorDiv.innerHTML = '<span class="material-symbols-rounded" style="font-size:32px;opacity:0.3">photo_camera</span>';
                  e.target.parentElement.appendChild(errorDiv);
                }}
              />
              {(image.is_profile || image.filename === profileImage) && (
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  left: '8px',
                  background: 'var(--primary)',
                  color: '#000',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '10px',
                  fontWeight: '600'
                }}>
                  PROFILE
                </div>
              )}
              {image.caption && (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '8px',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.9))',
                  color: '#ffffff',
                  fontSize: '11px',
                  textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                  fontWeight: '500'
                }}>
                  {image.caption}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '24px'
          }}
          onClick={() => {
            setSelectedImage(null);
            setEditingCaption(null);
          }}
        >
          <div
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getImageUrl(selectedImage)}
              alt={selectedImage.caption || 'Bike image'}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: '12px'
              }}
            />
            
            <div style={{
              marginTop: '16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              {/* Caption */}
              {editingCaption === selectedImage.id ? (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={captionText}
                    onChange={(e) => setCaptionText(e.target.value)}
                    placeholder="Add caption..."
                    maxLength={500}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(26, 26, 62, 0.9)',
                      color: '#ffffff',
                      width: '300px'
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => handleUpdateCaption(selectedImage.id)}
                    className="btn btn-sm btn-primary"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingCaption(null);
                      setCaptionText('');
                    }}
                    className="btn btn-sm btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div style={{ color: '#ffffff', fontSize: '14px', textAlign: 'center' }}>
                  {selectedImage.caption || (canEdit ? (
                    <button
                      onClick={() => {
                        setEditingCaption(selectedImage.id);
                        setCaptionText(selectedImage.caption || '');
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#ffffff',
                        cursor: 'pointer',
                        fontSize: '14px',
                        textDecoration: 'underline'
                      }}
                    >
                      + Add caption
                    </button>
                  ) : null)}
                </div>
              )}

              {/* Info */}
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
                {selectedImage.uploaded_by_name && `Uploaded by: ${selectedImage.uploaded_by_name}`}
                {selectedImage.created_at && ` | ${new Date(selectedImage.created_at).toLocaleDateString('en-GB')}`}
              </div>

              {/* Actions */}
              {canEdit && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  {!selectedImage.is_profile && selectedImage.filename !== profileImage && (
                    <button
                      onClick={() => handleSetAsProfile(selectedImage.id)}
                      className="btn btn-sm btn-secondary"
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon name="star" color="warning" darkMode={true} size={14} />
                        Set as profile image
                      </span>
                    </button>
                  )}
                  {canEdit && editingCaption !== selectedImage.id && (
                    <button
                      onClick={() => {
                        setEditingCaption(selectedImage.id);
                        setCaptionText(selectedImage.caption || '');
                      }}
                      className="btn btn-sm btn-secondary"
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon name="edit" color="primary" darkMode={true} size={14} />
                        Caption
                      </span>
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteImage(selectedImage.id)}
                    className="btn btn-sm"
                    style={{
                      background: 'rgba(239, 68, 68, 0.2)',
                      color: '#f87171',
                      border: '1px solid rgba(239, 68, 68, 0.3)'
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Icon name="delete" color="error" darkMode={true} size={14} />
                      Delete
                    </span>
                  </button>
                </div>
              )}
            </div>

            {/* Close button */}
            <button
              onClick={() => {
                setSelectedImage(null);
                setEditingCaption(null);
              }}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'var(--bg-overlay-light)',
                border: 'none',
                color: 'var(--text-primary)',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                fontSize: '20px',
                cursor: 'pointer'
              }}
            >
              X
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BikeImageGallery;

